<template>
  <div class="vehicle-grid-page">
    <div class="vehicle-grid-card">
      <div class="vehicle-grid-card__header">
        <div class="vehicle-grid-title">
          <div class="vehicle-grid-title__icon">
            <i class="fa fa-truck" />
          </div>
          <div class="vehicle-grid-title__text">
            <h2>{{ $t('components.vehicles.tabs.vehicles') }}</h2>
          </div>
        </div>
      </div>

      <div class="vehicle-grid-card__body">
        <vehicle-modal
          ref="vehicleModalRef"
          @emitted-refresh-grid="refreshGrid"
        />

        <DxDataGrid
          :ref="dataGridRef"
          :allow-column-reordering="true"
          :allow-column-resizing="true"
          :data-source="store"
          :hover-state-enabled="true"
          :remote-operations="remoteOperations"
          :show-borders="true"
          :show-column-lines="true"
          :scrolling="{ mode: 'standard', useNative: true }"
          class="vehicle-grid"
          height="auto"
          @content-ready="onContentReady"
        >
          <DxHeaderFilter :visible="false" />
          <DxPaging
            :page-index="0"
            :page-size="25"
          />
          <DxPager
            :allowed-page-sizes="[10, 20, 25, 50, 100]"
            :show-info="true"
            :show-navigation-buttons="true"
            :show-page-size-selector="true"
          />
          <DxFilterRow :visible="true" />
          <DxColumnFixing :enabled="true" />

          <DxColumn
            :allow-hiding="false"
            :caption="$t('components.vehicles.dataGrids.vehicle')"
            :filter-operations="allowedOperations"
            cell-template="vehicle-cell-template"
            data-field="vehicleLabel"
            width="16.6%"
          />
          <DxColumn
            :caption="$t('components.vehicles.dataGrids.driver')"
            :filter-operations="allowedOperations"
            cell-template="driver-cell-template"
            data-field="driverFullName"
            width="20.6%"
          />
          <DxColumn
            :allow-filtering="false"
            :allow-sorting="false"
            :caption="$t('components.vehicles.dataGrids.status')"
            cell-template="status-cell-template"
            css-class="vehicle-grid__status-column"
            width="16.6%"
          />
          <DxColumn
            :allow-filtering="false"
            :allow-sorting="false"
            :caption="$t('components.vehicles.dataGrids.icon')"
            cell-template="icon-cell-template"
            css-class="vehicle-grid__icon-column"
            width="16.6%"
          />
          <DxColumn
            :allow-filtering="false"
            :caption="$t('components.vehicles.dataGrids.location')"
            cell-template="location-cell-template"
            css-class="vehicle-grid__location-column"
            width="16.6%"
          />
          <DxColumn
            v-if="adminOrBoss"
            :allow-filtering="false"
            :allow-hiding="false"
            :allow-sorting="false"
            :caption="$t('components.vehicles.dataGrids.edit')"
            cell-template="edit-cell-template"
            css-class="vehicle-grid__edit-column"
            width="12.6%"
          />

          <template #status-cell-template="{ data }">
            <div class="vehicle-status-stack">
              <span
                v-if="data.data.sent_geo"
                class="vehicle-status-pill vehicle-status-pill--info"
              >
                <span class="fa fa-globe" />
                {{ $t('components.vehicles.dataGrids.sentgeo') }}
              </span>
              <span
                :class="statusClass(data.data.device?.etoll)"
                class="vehicle-status-pill"
              >
                <span
                  :class="statusIconClass(data.data.device?.etoll)"
                  class="fa"
                />
                {{ $t('components.vehicles.dataGrids.etoll') }}
              </span>
              <span
                :class="statusClass(data.data.device?.has_garmin)"
                class="vehicle-status-pill"
              >
                <span
                  :class="statusIconClass(data.data.device?.has_garmin)"
                  class="fa"
                />
                {{ $t('components.vehicles.dataGrids.garmin') }}
              </span>
              <span
                :class="statusClass(data.data.device?.has_tacho)"
                class="vehicle-status-pill"
              >
                <span
                  :class="statusIconClass(data.data.device?.has_tacho)"
                  class="fa"
                />
                {{ $t('components.vehicles.dataGrids.tachograph') }}
              </span>
            </div>
          </template>

          <template #vehicle-cell-template="{ data }">
            <div class="vehicle-main-cell">
              <p class="fw-bold fs-2 reduced-margin-bottom">
                {{ getVehicleDisplayName(data.data) }}
              </p>
              <p
                v-if="data.data.registration && data.data.vars?.name"
                class="fs-6 reduced-margin-bottom"
              >
                {{ data.data.registration }}
              </p>
              <p
                v-if="data.data.device?.imei"
                class="fs-6 reduced-margin-bottom"
              >
                <b>IMEI:</b> {{ data.data.device.imei }}
              </p>
              <p
                v-if="data.data.vin"
                class="fs-6 reduced-margin-bottom"
              >
                <b>{{ $t('components.vehicles.dataGrids.vinNumber') }}:</b> {{ data.data.vin }}
              </p>
              <p class="fs-6 reduced-margin-bottom">
                <b>{{ $t('components.vehicles.dataGrids.tankCapacity') }}:</b>
                {{ getTankCapacity(data.data) }} L
              </p>
              <p
                v-if="data.data.vars?.phone"
                class="fs-6 reduced-margin-bottom"
              >
                <b>{{ $t('components.vehicles.dataGrids.phoneNumber') }}:</b> {{ data.data.vars.phone }}
              </p>
              <p
                v-if="data.data.vars?.virtual_odometer"
                class="fs-6 reduced-margin-bottom"
              >
                <b>{{ $t('components.vehicles.dataGrids.virtualOdometer') }}:</b>
                {{ data.data.vars.virtual_odometer }} km
              </p>
            </div>
          </template>

          <template #driver-cell-template="{ data }">
            <div class="vehicle-driver-cell">
              <div class="vehicle-driver-cell__name">
                <span class="fa fa-user" />
                {{ getDriverName(data.data) }}
              </div>
              <div class="vehicle-driver-cell__cards">
                <span class="fa fa-credit-card" />
                {{ getDriverCards(data.data) }}
              </div>
            </div>
          </template>

          <template #location-cell-template="{ data }">
            <span
              v-if="!data.data.dataLastCoordinates"
              class="vehicle-muted"
            >—</span>
            <a
              v-else
              :href="mapsUrl(data.data.dataLastCoordinates)"
              target="_blank"
              rel="noopener"
            >
              {{
                addrCache[data.data.device.imei]
                  || `${Number(data.data.dataLastCoordinates.lat).toFixed(6)}, ${Number(data.data.dataLastCoordinates.lng).toFixed(6)}`
              }}
            </a>
          </template>

          <template #icon-cell-template="{ data }">
            <img
              :src="getIconSrc(data.data.vars?.icon_id)"
              :alt="`Icon ${data.data.registration}`"
              class="vehicle-map-icon"
            >
          </template>

          <template #edit-cell-template="{ data }">
            <button
              :title="$t('components.vehicles.dataGrids.edit')"
              class="vehicle-icon-button"
              type="button"
              @click="openEditModal(data)"
            >
              <i class="fa fa-edit" />
            </button>
          </template>
        </DxDataGrid>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import CustomStore from 'devextreme/data/custom_store'
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import {getMyVehiclesGrid, getReverseGeocode, getSentGeoActive} from '@/crud/getters'
import VehicleModal from '@/components/content/_modals/VehicleModal.vue'
import {checkIfAdminOrBoss} from '@/utils/tools'
import {
  buildVehicleGridLoadParams,
  normalizeVehicleGridResponse
} from '@/services/vehicles/grid-store'

export default defineComponent({
  name: 'Vehicles',
  components: {
    DxDataGrid,
    DxHeaderFilter,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxColumnFixing,
    DxColumn,
    VehicleModal
  },
  data(): {
    store: CustomStore | null,
    allowedOperations: Array<string>,
    remoteOperations: Record<string, boolean>,
    dataGridRef: string,
    adminOrBoss: boolean,
    sentGeoImei: Array<string | number>,
    addrCache: Record<string, string>,
    dataGridReady: boolean,
    isMounted: boolean,
  } {
    return {
      store: null,
      allowedOperations: ['contains', '=', 'notcontains', '!='],
      remoteOperations: {paging: true, sorting: true, filtering: true},
      dataGridRef: 'vehiclesGrid',
      adminOrBoss: false,
      sentGeoImei: [],
      addrCache: {},
      dataGridReady: false,
      isMounted: false
    }
  },
  computed: {
    dataGrid() {
      const self: any = this
      return self.$refs[self.dataGridRef]?.instance || null
    }
  },
  methods: {
    initStore(): void {
      const self = this

      this.store = new CustomStore({
        key: 'id',
        async load(loadOptions: any) {
          await self.loadSentGeoActive()

          const response = await getMyVehiclesGrid(buildVehicleGridLoadParams(loadOptions))
          const result = normalizeVehicleGridResponse(response.data)

          result.data.forEach((vehicle: any) => {
            vehicle.sent_geo = self.sentGeoImei.includes(vehicle.device?.imei)
              || self.sentGeoImei.includes(Number(vehicle.device?.imei))
            self.prefetchReverse(vehicle)
          })

          return result
        }
      })
    },

    async loadSentGeoActive(): Promise<void> {
      try {
        const response = await getSentGeoActive()
        this.sentGeoImei = response.data || []
      } catch {
        this.sentGeoImei = []
      }
    },

    refreshGrid(): void {
      if (this.dataGrid && typeof this.dataGrid.refresh === 'function') {
        this.dataGrid.refresh()
      }
    },

    onContentReady(): void {
      this.dataGridReady = true
    },

    mapsUrl(coords: { lat: number | string, lng: number | string }): string {
      const lat = Number(coords.lat)
      const lng = Number(coords.lng)
      return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    },

    prefetchReverse(vehicle: any): void {
      const imei = vehicle?.device?.imei
      const coordinates = vehicle?.dataLastCoordinates
      if (!imei || !coordinates || this.addrCache[imei] !== undefined) return

      this.addrCache[imei] = ''

      getReverseGeocode(`${coordinates.lat},${coordinates.lng}`)
        .then((res: any) => {
          this.addrCache[imei] = res.data || ''
          if (!this.isMounted) return

          this.$nextTick(() => {
            if (
              this.isMounted
              && this.dataGridReady
              && this.dataGrid
              && typeof this.dataGrid.repaint === 'function'
            ) {
              this.dataGrid.repaint()
            }
          })
        })
        .catch(() => {
          this.addrCache[imei] = ''
        })
    },

    getIconSrc(id: unknown): string {
      const idx = Number(id) || 0
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      return require(`@/assets/img/map/icons/${idx}.png`)
    },

    openEditModal(data: any): void {
      const self: any = this
      self.$refs.vehicleModalRef.openEditModal(data.row.data)
    },

    getVehicleDisplayName(vehicle: any): string {
      return vehicle?.vars?.name || vehicle?.registration || '—'
    },

    getTankCapacity(vehicle: any): number {
      return Number(vehicle?.tank_capacity || 0) + Number(vehicle?.tank_capacity2 || 0)
    },

    getDriverName(vehicle: any): string {
      if (!vehicle?.driver) {
        return this.$t('components.vehicles.dataGrids.noDriver') as string
      }

      return `${vehicle.driver.name || ''} ${vehicle.driver.surname || ''}`.trim()
    },

    getDriverCards(vehicle: any): string {
      const card1 = vehicle?.driversCardInfo?.driverCard1?.user
      const card2 = vehicle?.driversCardInfo?.driverCard2?.user

      if (card1 || card2) {
        return [card1, card2]
          .filter(Boolean)
          .map((user: any, index: number) => `(${index + 1}) ${user.driver_card} - ${user.name} ${user.surname}`)
          .join(', ')
      }

      return vehicle?.driver?.driver_card || this.$t('components.vehicles.dataGrids.noDriverCard') as string
    },

    statusClass(value: unknown): Record<string, boolean> {
      return {
        'vehicle-status-pill--success': !!value,
        'vehicle-status-pill--danger': !value
      }
    },

    statusIconClass(value: unknown): Record<string, boolean> {
      return {
        'fa-check': !!value,
        'fa-exclamation': !value
      }
    }
  },
  mounted(): void {
    this.isMounted = true
    this.adminOrBoss = checkIfAdminOrBoss()
    this.initStore()
  },
  beforeUnmount(): void {
    this.isMounted = false
    this.dataGridReady = false
  }
})
</script>

<style lang="scss" src="@/assets/scss/vehicles-grid.scss"></style>
